import { env } from "@acme/env";
import {
  PRO_BUNDLE_PRICE_ID,
  PRO_PLUS_BUNDLE_PRICE_ID,
  PICSTUDIO_DRIVE_PRICE_ID,
  DO_OVER_PRICE_ID,
  PRICE_ID_REVENUE,
} from "../constants";

export const STARTER_KIT_PRODUCT_NAME = "Starter Kit";
export const PRO_BUNDLE_PRODUCT_NAME = "Pro Bundle";
export const FRIENDS_BUNDLE_PRODUCT_NAME = "Friends Bundle";
export const PICSTUDIO_DRIVE_PRODUCT_NAME = "PicStudio Drive";
export const PICSTUDIO_DRIVE_YEARLY_PRODUCT_NAME = "PicStudio Drive (Yearly)";
export const PRO_PLUS_BUNDLE_PRODUCT_NAME = "Pro+ Bundle";
export const DO_OVER_PRODUCT_NAME = "Do-Over";
export const SINGLE_PHOTO_PACK_PRODUCT_NAME = "Single Photo Pack";
export const THREE_PHOTO_PACK_BUNDLE_PRODUCT_NAME = "3 Photo Pack Bundle";
export const FIVE_PHOTO_PACK_BUNDLE_PRODUCT_NAME = "5 Photo Pack Bundle";
export const TEN_PHOTO_PACK_BUNDLE_PRODUCT_NAME = "10 Photo Pack Bundle";

export const STRIPE_PRODUCTS_CREDITS = {
  [env.NEXT_PUBLIC_STRIPE_STARTER_PRICE_ID as string]: {
    models: 1,
    photoPacks: 1,
    productName: STARTER_KIT_PRODUCT_NAME,
    productType: "initial-purchase",
  },
  [PRO_BUNDLE_PRICE_ID as string]: {
    models: 1,
    photoPacks: 3,
    productName: PRO_BUNDLE_PRODUCT_NAME,
    productType: "initial-purchase",
  },
  [PICSTUDIO_DRIVE_PRICE_ID as string]: {
    models: 0,
    photoPacks: 1,
    productName: PICSTUDIO_DRIVE_PRODUCT_NAME,
    productType: "initial-purchase",
  },
  [PRO_PLUS_BUNDLE_PRICE_ID as string]: {
    models: 1,
    photoPacks: 5,
    productName: PRO_PLUS_BUNDLE_PRODUCT_NAME,
    productType: "initial-purchase",
  },
  [DO_OVER_PRICE_ID as string]: {
    models: 0,
    photoPacks: 1,
    redoCredits: 1,
    productName: DO_OVER_PRODUCT_NAME,
    productType: "add-on",
  },
  [env.NEXT_PUBLIC_STRIPE_SINGLE_PACK_PRICE_ID as string]: {
    models: 0,
    photoPacks: 1,
    productName: SINGLE_PHOTO_PACK_PRODUCT_NAME,
    productType: "add-on",
  },
  [env.NEXT_PUBLIC_STRIPE_THREE_PHOTO_PACK_BUNDLE_PRICE_ID as string]: {
    models: 0,
    photoPacks: 3,
    productName: THREE_PHOTO_PACK_BUNDLE_PRODUCT_NAME,
    productType: "add-on",
  },
  [env.NEXT_PUBLIC_STRIPE_FIVE_PHOTO_PACK_BUNDLE_PRICE_ID as string]: {
    models: 0,
    photoPacks: 5,
    productName: FIVE_PHOTO_PACK_BUNDLE_PRODUCT_NAME,
    productType: "add-on",
  },
  [env.NEXT_PUBLIC_STRIPE_TEN_PHOTO_PACK_BUNDLE_PRICE_ID as string]: {
    models: 0,
    photoPacks: 10,
    productName: TEN_PHOTO_PACK_BUNDLE_PRODUCT_NAME,
    productType: "add-on",
  },
};

interface StripeProduct {
  priceId: string;
  models: number;
  photoPacks: number;
  redoCredits?: number;
  productName: string;
  discount: number;
  photos?: number;
  thumbnail?: string;
  defaultReturnUrl?: string;
  price?: number;
  originalPrice?: number;
  amount?: number;
  disabled?: boolean;
  features?: string[];
  mostPopular?: boolean;
  pricePerPhoto?: string;
  pricePerPhotoAmount?: number;
}

export const STRIPE_PRODUCTS_CREDITS_BY_TYPE: Record<string, StripeProduct[]> =
  {
    "initial-purchase": [
      {
        priceId: env.NEXT_PUBLIC_STRIPE_STARTER_PRICE_ID as string,
        models: 1,
        photoPacks: 1,
        productName: STARTER_KIT_PRODUCT_NAME,
        discount: 0,
        thumbnail:
          "https://imagedelivery.net/9sCnq8t6WEGNay0RAQNdvQ/fbf5f12f-3e3c-4fd5-ba73-e4a14083f800/optimized",
        features: [
          "One person",
          "40 photos", // price per photo
          "One photo pack",
          "Delivered in 30 mins",
          "Additional packs just $" +
            PRICE_ID_REVENUE[
              env.NEXT_PUBLIC_STRIPE_SINGLE_PACK_PRICE_ID as string
            ],
        ],
        pricePerPhoto: "$0.75 / photo",
        pricePerPhotoAmount: 0.75,
        mostPopular: false,
        price: PRICE_ID_REVENUE[env.NEXT_PUBLIC_STRIPE_STARTER_PRICE_ID],
      },
      {
        priceId: PRO_BUNDLE_PRICE_ID as string,
        models: 1,
        photoPacks: 3,
        productName: PRO_BUNDLE_PRODUCT_NAME,
        discount: 0,
        thumbnail:
          "https://imagedelivery.net/9sCnq8t6WEGNay0RAQNdvQ/96f5426d-18f5-4bac-a638-ca4b405fa900/optimized",
        features: [
          "One person",
          "120 photos",
          "Three photo packs",
          "Delivered in 30 mins",
          "Additional packs just $" +
            PRICE_ID_REVENUE[
              env.NEXT_PUBLIC_STRIPE_SINGLE_PACK_PRICE_ID as string
            ],
        ],
        pricePerPhoto: "$0.33 / photo",
        pricePerPhotoAmount: 0.33,
        mostPopular: true,
        price: PRICE_ID_REVENUE[PRO_BUNDLE_PRICE_ID],
      },
      {
        priceId: PRO_PLUS_BUNDLE_PRICE_ID as string,
        models: 1,
        photoPacks: 5,
        productName: PRO_PLUS_BUNDLE_PRODUCT_NAME,
        discount: 0,
        thumbnail:
          "https://imagedelivery.net/9sCnq8t6WEGNay0RAQNdvQ/f4eb55a6-8f28-47f7-dfc2-804bcb8fe200/optimized",
        features: [
          "One person",
          "200 photos",
          "Five photo packs",
          "Delivered in 30 mins",
          "Additional packs just $" +
            PRICE_ID_REVENUE[
              env.NEXT_PUBLIC_STRIPE_SINGLE_PACK_PRICE_ID as string
            ],
        ],
        pricePerPhoto: "$0.30 / photo",
        pricePerPhotoAmount: 0.3,
        mostPopular: false,
        price: PRICE_ID_REVENUE[PRO_PLUS_BUNDLE_PRICE_ID],
      },
    ],
    "add-on": [
      {
        priceId: env.NEXT_PUBLIC_STRIPE_SINGLE_PACK_PRICE_ID as string,
        models: 0,
        photoPacks: 1,
        productName: SINGLE_PHOTO_PACK_PRODUCT_NAME,
        defaultReturnUrl: "/packs",
        price: 9.99,
        photos: 40,
        discount: 0,
        originalPrice: 9.99,
        amount: 1,
        thumbnail:
          "https://imagedelivery.net/9sCnq8t6WEGNay0RAQNdvQ/fbf5f12f-3e3c-4fd5-ba73-e4a14083f800/optimized",
      },
      {
        priceId:
          env.NEXT_PUBLIC_STRIPE_THREE_PHOTO_PACK_BUNDLE_PRICE_ID as string,
        models: 0,
        photoPacks: 3,
        productName: THREE_PHOTO_PACK_BUNDLE_PRODUCT_NAME,
        defaultReturnUrl: "/packs",
        price: 26.99,
        photos: 120,
        discount: 10,
        originalPrice: 29.97,
        amount: 1,
        thumbnail:
          "https://imagedelivery.net/9sCnq8t6WEGNay0RAQNdvQ/96f5426d-18f5-4bac-a638-ca4b405fa900/optimized",
      },

      {
        priceId:
          env.NEXT_PUBLIC_STRIPE_FIVE_PHOTO_PACK_BUNDLE_PRICE_ID as string,
        models: 0,
        photoPacks: 5,
        productName: FIVE_PHOTO_PACK_BUNDLE_PRODUCT_NAME,
        defaultReturnUrl: "/packs",
        price: 39.99,
        photos: 200,
        discount: 20,
        originalPrice: 49.95,
        amount: 1,
        thumbnail:
          "https://imagedelivery.net/9sCnq8t6WEGNay0RAQNdvQ/f4eb55a6-8f28-47f7-dfc2-804bcb8fe200/optimized",
      },
      {
        priceId:
          env.NEXT_PUBLIC_STRIPE_TEN_PHOTO_PACK_BUNDLE_PRICE_ID as string,
        models: 0,
        photoPacks: 10,
        productName: TEN_PHOTO_PACK_BUNDLE_PRODUCT_NAME,
        defaultReturnUrl: "/packs",
        price: 69.99,
        photos: 400,
        discount: 30,
        originalPrice: 99.9,
        amount: 1,
        thumbnail:
          "https://imagedelivery.net/9sCnq8t6WEGNay0RAQNdvQ/df2aef76-6dba-405f-f44b-f4b30564de00/optimized",
      },
    ],
    subscription: [
      {
        priceId: env.NEXT_PUBLIC_STRIPE_PICSTUDIO_DRIVE_PRICE_ID as string,
        models: 0,
        photoPacks: 0,
        productName: PICSTUDIO_DRIVE_PRODUCT_NAME,
        discount: 0,
        price: 9.99,
        features: [
          "Unlimited storage",
          "Access to all your models",
          "Share with friends & family",
          "Monthly billing",
        ],
        defaultReturnUrl: "/dashboard",
        mostPopular: false,
      },
      {
        priceId:
          env.NEXT_PUBLIC_STRIPE_PICSTUDIO_DRIVE_YEARLY_PRICE_ID as string,
        models: 0,
        photoPacks: 1,
        productName: PICSTUDIO_DRIVE_YEARLY_PRODUCT_NAME,
        discount: 20,
        price: 96,
        originalPrice: 119.88,
        features: [
          "Unlimited storage",
          "Access to all your models",
          "Share with friends & family",
          "Annual billing (save 16%)",
          "2 months free",
        ],
        defaultReturnUrl: "/dashboard",
        mostPopular: true,
      },
    ],
    miscellaneous: [
      {
        priceId: DO_OVER_PRICE_ID as string,
        models: 0,
        photoPacks: 1,
        redoCredits: 1,
        productName: DO_OVER_PRODUCT_NAME,
        discount: 0,
      },
    ],
  } as const;

export const getProductsByType = (
  type: "initial-purchase" | "add-on" | "subscription" | "miscellaneous"
) => {
  return STRIPE_PRODUCTS_CREDITS_BY_TYPE[
    type as keyof typeof STRIPE_PRODUCTS_CREDITS_BY_TYPE
  ]?.filter((product) => !product.disabled);
};

export type ProductName =
  | typeof STARTER_KIT_PRODUCT_NAME
  | typeof PRO_BUNDLE_PRODUCT_NAME
  | typeof FRIENDS_BUNDLE_PRODUCT_NAME
  | typeof PICSTUDIO_DRIVE_PRODUCT_NAME
  | typeof PICSTUDIO_DRIVE_YEARLY_PRODUCT_NAME
  | typeof PRO_PLUS_BUNDLE_PRODUCT_NAME
  | typeof DO_OVER_PRODUCT_NAME
  | typeof SINGLE_PHOTO_PACK_PRODUCT_NAME
  | typeof THREE_PHOTO_PACK_BUNDLE_PRODUCT_NAME
  | typeof FIVE_PHOTO_PACK_BUNDLE_PRODUCT_NAME
  | typeof TEN_PHOTO_PACK_BUNDLE_PRODUCT_NAME;

export const getProductByName = (
  name: ProductName
): StripeProduct | undefined => {
  for (const type in STRIPE_PRODUCTS_CREDITS_BY_TYPE) {
    const productArray =
      STRIPE_PRODUCTS_CREDITS_BY_TYPE[
        type as keyof typeof STRIPE_PRODUCTS_CREDITS_BY_TYPE
      ];
    if (productArray) {
      const product = productArray.find((p) => p.productName === name);
      if (product) return product;
    }
  }
  return undefined;
};

export interface ProductDetails {
  photoPackCount: number;
  photoCount: number;
}

export const getProductDetailsByName = (
  productName: string
): ProductDetails => {
  // Default values if product not found
  const defaultDetails: ProductDetails = {
    photoPackCount: 0,
    photoCount: 0,
  };

  // Try to find exact match by product name
  const product = getProductByName(productName as ProductName);
  if (product) {
    return {
      photoPackCount: product.photoPacks,
      photoCount: product.photoPacks * 40,
    };
  }

  // If not found by exact match, search through all products
  for (const type in STRIPE_PRODUCTS_CREDITS_BY_TYPE) {
    const productArray =
      STRIPE_PRODUCTS_CREDITS_BY_TYPE[
        type as keyof typeof STRIPE_PRODUCTS_CREDITS_BY_TYPE
      ];

    if (productArray) {
      // Try partial match
      const partialMatch = productArray.find(
        (p) =>
          productName.includes(p.productName) ||
          p.productName.includes(productName)
      );

      if (partialMatch) {
        return {
          photoPackCount: partialMatch.photoPacks,
          photoCount: partialMatch.photoPacks * 40,
        };
      }
    }
  }

  return defaultDetails;
};
